import { ListItemNode, ListNode } from '@lexical/list'
import { HeadingNode } from '@lexical/rich-text'
import { TableNode } from '@lexical/table'
import * as docx from 'docx'
import { $getRoot, $isElementNode, $isTextNode, ElementNode, LexicalNode } from 'lexical'

interface SyntaxHighlightCodeNodeProps {
  language?: string
  rawMarkdown: string
  getTextContent(): string
}

const DEFAULT_IMAGE_DIMENSIONS = { width: 600, height: 400 }

function normalizeImageDimensions(dimensions: { width: number; height: number }): {
  width: number
  height: number
} {
  if (
    Number.isFinite(dimensions.width) &&
    Number.isFinite(dimensions.height) &&
    dimensions.width > 0 &&
    dimensions.height > 0
  ) {
    return {
      width: Math.round(dimensions.width),
      height: Math.round(dimensions.height)
    }
  }
  return DEFAULT_IMAGE_DIMENSIONS
}

export function nodes2docx(): docx.Document {
  const root = $getRoot()
  const topLevelChildren = root.getChildren()

  let listCounter = 0
  const listInstanceMap = new Map<ListNode, string>()

  const numberingConfig = {
    config: [
      {
        reference: 'continuous-numbering',
        levels: Array.from({ length: 9 }, (_, i) => ({
          level: i,
          text: i === 0 ? '%1. ' : `%${i + 1}. `,
          format: i === 0 ? docx.LevelFormat.DECIMAL : docx.LevelFormat.LOWER_LETTER,
          style: {
            paragraph: {
              indent: {
                left: (i + 1) * 360,
                hanging: 240
              },
              spacing: {
                before: 120,
                after: 120
              }
            }
          },
          alignment: docx.AlignmentType.LEFT,
          start: 1
        }))
      }
    ]
  }

  const getNumberingReference = (listNode: ListNode): string => {
    if (!listInstanceMap.has(listNode)) {
      const reference = `list-instance-${++listCounter}`
      listInstanceMap.set(listNode, reference)

      numberingConfig.config.push({
        reference,
        levels: Array.from({ length: 9 }, (_, i) => ({
          level: i,
          text: i === 0 ? '%1. ' : `%${i + 1}. `,
          format: i === 0 ? docx.LevelFormat.DECIMAL : docx.LevelFormat.LOWER_LETTER,
          style: {
            paragraph: {
              indent: {
                left: (i + 1) * 360,
                hanging: 240
              },
              spacing: {
                before: 120,
                after: 120
              }
            }
          },
          alignment: docx.AlignmentType.LEFT,
          start: 1
        }))
      })
    }
    return listInstanceMap.get(listNode)!
  }

  const convertListNode = (
    node: LexicalNode,
    level = 0,
    paragraphs: docx.Paragraph[] = []
  ): void => {
    if (node instanceof ListItemNode) {
      const textRuns: docx.TextRun[] = []
      const processTextContent = (child: LexicalNode) => {
        if ($isTextNode(child)) {
          textRuns.push(convertTextNode(child, false))
        } else if ($isElementNode(child) && !(child instanceof ListNode)) {
          child.getChildren().forEach(processTextContent)
        }
      }

      node.getChildren().forEach(processTextContent)

      if (textRuns.length > 0) {
        const parentList = node.getParent()
        const isUnorderedList = (parentList as any).getTag?.() === 'ul'

        // Get the specific numbering reference for this list instance
        const numberingReference =
          parentList instanceof ListNode
            ? getNumberingReference(parentList)
            : 'continuous-numbering'

        const paragraph = new docx.Paragraph({
          children: textRuns,
          bullet: isUnorderedList
            ? {
                level: Math.min(level, 8)
              }
            : undefined,
          numbering: !isUnorderedList
            ? {
                reference: numberingReference,
                level: Math.min(level, 8)
              }
            : undefined,
          spacing: {
            before: 120,
            after: 120
          }
        })
        paragraphs.push(paragraph)
      }

      const nestedLists = node.getChildren().filter(child => child instanceof ListNode)
      nestedLists.forEach(list => convertListNode(list, level + 1, paragraphs))
    } else if (node instanceof ListNode) {
      node.getChildren().forEach(child => convertListNode(child, level, paragraphs))
    }
  }

  const base64ToBinary = (base64: string): Uint8Array => {
    const base64Data = base64.includes('base64,') ? base64.split('base64,')[1] : base64

    const binaryString = atob(base64Data)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return bytes
  }

  function getImageDimensionsFromBase64Sync(base64String: string): {
    width: number
    height: number
  } {
    try {
      const base64Data = base64String.includes('base64,')
        ? base64String.split('base64,')[1]
        : base64String

      const binaryString = atob(base64Data)
      const bytes = new Uint8Array(binaryString.length)
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }

      if (bytes[0] === 0xff && bytes[1] === 0xd8) {
        return getJPEGDimensions(bytes)
      } else if (bytes[0] === 0x89 && bytes[1] === 0x50) {
        return getPNGDimensions(bytes)
      } else {
        return DEFAULT_IMAGE_DIMENSIONS
      }
    } catch (error) {
      console.error('Error parsing image dimensions:', error)
      return DEFAULT_IMAGE_DIMENSIONS
    }
  }

  function getJPEGDimensions(bytes: Uint8Array): { width: number; height: number } {
    let pos = 2
    while (pos < bytes.length) {
      if (bytes[pos] !== 0xff) {
        pos++
        continue
      }

      if (bytes[pos + 1] === 0xc0 || bytes[pos + 1] === 0xc2) {
        const height = (bytes[pos + 5] << 8) | bytes[pos + 6]
        const width = (bytes[pos + 7] << 8) | bytes[pos + 8]
        return normalizeImageDimensions({ width, height })
      }

      pos += 2 + ((bytes[pos + 2] << 8) | bytes[pos + 3])
    }

    return DEFAULT_IMAGE_DIMENSIONS
  }

  function getPNGDimensions(bytes: Uint8Array): { width: number; height: number } {
    const width = (bytes[16] << 24) | (bytes[17] << 16) | (bytes[18] << 8) | bytes[19]
    const height = (bytes[20] << 24) | (bytes[21] << 16) | (bytes[22] << 8) | bytes[23]
    return normalizeImageDimensions({ width, height })
  }

  const convertImageNode = (node: LexicalNode): docx.Paragraph => {
    try {
      const src = (node as any).getSrc?.() || (node as any).src || ''
      if (typeof src === 'string' && src.length > 0) {
        let imageData: Uint8Array
        if (src.startsWith('data:image')) {
          imageData = base64ToBinary(src)
        } else {
          imageData = base64ToBinary(src)
        }

        const dimensions = normalizeImageDimensions(getImageDimensionsFromBase64Sync(src))
        const aspectRatio = dimensions.width / dimensions.height
        const maxWidth = 600
        const newHeight = Math.round(maxWidth / aspectRatio)

        return new docx.Paragraph({
          children: [
            new docx.ImageRun({
              data: imageData,
              transformation: {
                width: maxWidth,
                height: newHeight
              }
            })
          ],
          spacing: {
            before: 240,
            after: 240
          },
          alignment: docx.AlignmentType.CENTER
        })
      }
    } catch (error) {
      console.error('Error converting image:', error)
    }

    return new docx.Paragraph({ children: [] })
  }

  const convertTextNode = (node: LexicalNode, isHeading: boolean): docx.TextRun => {
    if ($isTextNode(node)) {
      if (isHeading) {
        return new docx.TextRun({
          text: node.getTextContent(),
          bold: true,
          color: '000000'
        })
      }
      return new docx.TextRun({
        text: node.getTextContent(),
        bold: node.hasFormat('bold'),
        italics: node.hasFormat('italic'),
        font: node.hasFormat('code') ? 'Courier New' : ''
      })
    }
    return new docx.TextRun('')
  }

  const convertNode = (
    node: LexicalNode,
    isHeading = false
  ): docx.ParagraphChild | docx.Paragraph | docx.Table | docx.Paragraph[] => {
    if ($isTextNode(node)) {
      return convertTextNode(node, isHeading)
    } else if (node instanceof ListNode) {
      const listItems: docx.Paragraph[] = []
      convertListNode(node, 0, listItems)
      return listItems
    } else if (node.getType() === 'image') {
      return convertImageNode(node)
    } else if (node instanceof TableNode) {
      const rows: docx.TableRow[] = []
      const allRows = node.getChildren()

      // First pass: collect content lengths for each column
      const columnWidths: number[] = []
      const maxContentLengths: number[] = []

      allRows.forEach(row => {
        const cells = (row as ElementNode).getChildren()
        cells.forEach((cell, columnIndex) => {
          const contentLength = cell.getTextContentSize()

          if (maxContentLengths[columnIndex] === undefined) {
            maxContentLengths[columnIndex] = contentLength
          } else {
            maxContentLengths[columnIndex] = Math.max(maxContentLengths[columnIndex], contentLength)
          }
        })
      })

      const totalContentLength = maxContentLengths.reduce((sum, length) => sum + length, 0)
      const totalWidth = 9000

      columnWidths.length = maxContentLengths.length
      maxContentLengths.forEach((contentLength, index) => {
        const width =
          totalContentLength > 0
            ? Math.floor((contentLength / totalContentLength) * totalWidth)
            : Math.floor(totalWidth / Math.max(maxContentLengths.length, 1))
        columnWidths[index] = width
      })

      // Second pass: create table with optimized widths
      for (const row of allRows) {
        const cells: docx.TableCell[] = []
        const cellNodes = (row as ElementNode).getChildren()

        cellNodes.forEach((cell, columnIndex) => {
          const childParagraphs = (cell as ElementNode).getChildren().map(childNode => {
            const converted = convertNode(childNode)
            if (converted instanceof Array) {
              return converted[0]
            }
            return converted instanceof docx.Paragraph
              ? converted
              : new docx.Paragraph({ children: [converted] })
          })

          cells.push(
            new docx.TableCell({
              children: childParagraphs
            })
          )
        })
        rows.push(new docx.TableRow({ children: cells }))
      }
      return new docx.Table({
        rows,
        width: {
          size: 100,
          type: docx.WidthType.AUTO
        },
        columnWidths: columnWidths
      })
    } else if ($isElementNode(node)) {
      const children: docx.ParagraphChild[] = []
      for (const child of node.getChildren()) {
        const converted = convertNode(child)
        if (converted instanceof docx.TextRun) {
          children.push(converted)
        } else if (converted instanceof Array) {
          return converted
        } else {
          return converted
        }
      }

      if (node instanceof HeadingNode) {
        const children: docx.ParagraphChild[] = []
        for (const child of node.getChildren()) {
          const converted = convertNode(child, true)
          if (converted instanceof docx.TextRun) {
            children.push(converted)
          }
        }
        const headingLevel = node.getTag()
        switch (headingLevel) {
          case 'h1':
            return new docx.Paragraph({ children, heading: docx.HeadingLevel.HEADING_1 })
          case 'h2':
            return new docx.Paragraph({ children, heading: docx.HeadingLevel.HEADING_2 })
          case 'h3':
            return new docx.Paragraph({ children, heading: docx.HeadingLevel.HEADING_3 })
          case 'h4':
            return new docx.Paragraph({ children, heading: docx.HeadingLevel.HEADING_4 })
          case 'h5':
            return new docx.Paragraph({ children, heading: docx.HeadingLevel.HEADING_5 })
          case 'h6':
            return new docx.Paragraph({ children, heading: docx.HeadingLevel.HEADING_6 })
        }
      }

      switch (node.getType()) {
        case 'quote':
          return new docx.Paragraph({
            children,
            border: {
              left: {
                color: '999999',
                space: 10,
                size: 20,
                style: 'single'
              }
            },
            shading: {
              type: docx.ShadingType.CLEAR,
              fill: 'FFFFE0'
            },
            spacing: { before: 240, after: 240 },
            indent: { left: 720, right: 720 }
          })
        case 'code':
          const codes: docx.ParagraphChild[] = []
          for (const child of node.getChildren()) {
            if (child.getType() === 'code-highlight') {
              codes.push(
                new docx.TextRun({
                  text: child.getTextContent(),
                  font: 'Courier New'
                })
              )
            } else if (child.getType() === 'linebreak') {
              codes.push(
                new docx.TextRun({
                  text: '\n',
                  break: 1
                })
              )
            }
          }
          return new docx.Paragraph({
            children: codes,
            shading: {
              type: docx.ShadingType.CLEAR,
              fill: 'E0E0E0'
            },
            spacing: { before: 240, after: 240 },
            indent: { left: 360 }
          })

        case 'editor-shiki-code': {
          const shikiNode = node as unknown as SyntaxHighlightCodeNodeProps
          const codeText = shikiNode.getTextContent()

          const lines = codeText.split(/\r?\n/)
          const paragraphs: docx.Paragraph[] = []

          lines.forEach((line, index) => {
            const children: docx.TextRun[] = []

            children.push(
              new docx.TextRun({
                text: line.replace(/\t/g, '    '),
                font: 'Courier New',
                size: 20,
                preserveSpace: true
              } as docx.IRunOptions)
            )

            paragraphs.push(
              new docx.Paragraph({
                children,
                spacing: {
                  before: index === 0 ? 120 : 0,
                  after: index === lines.length - 1 ? 120 : 0,
                  line: 240
                },
                indent: { left: 360 },
                shading: {
                  type: docx.ShadingType.CLEAR,
                  fill: 'F8F8F8'
                },
                keepNext: true,
                keepLines: true
              })
            )
          })
          return paragraphs
        }
        default:
          return new docx.Paragraph({ children })
      }
    }
    return new docx.TextRun('')
  }

  const sections: docx.ISectionOptions[] = [
    {
      properties: {},
      children: topLevelChildren.flatMap(child => {
        const converted = convertNode(child)
        if (converted instanceof docx.Paragraph || converted instanceof docx.Table) {
          return [converted]
        }
        if (converted instanceof Array) {
          return converted
        }
        return [new docx.Paragraph({ children: [converted] })]
      })
    }
  ]

  return new docx.Document({
    numbering: numberingConfig,
    sections
  })
}
